/**
 * Super-admin GeoSim toolbar — lives in anaafernandez (this brand).
 *
 * Production flow:
 *   1. Clerk super_admin / QA session
 *   2. /?geo_sim_boot=1 or Admin → "Simular visitante"
 *   3. Pick scenario / country · VPN Clean · Activar
 *   4. Hard nav with geo_sim=1&gst=<minted>&country=…&simulate_vpn=clean
 *
 * Never invent gst. Never treat ?country= alone as real edge override.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Copy,
  ExternalLink,
  Film,
  Globe2,
  LayoutDashboard,
  Loader2,
  MapPin,
  Settings2,
  Shield,
  Sparkles,
  X,
} from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useIsSuperAdmin } from "@/lib/auth";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import {
  CTA_POR_REGION_ADMIN_PATH,
  GEO_SIM_AR_PROVINCES,
  GEO_SIM_AR_QUICK_CITIES,
  GEO_SIM_QUICK_COUNTRIES,
  GEO_SIM_SCENARIO_PRESETS,
  GEO_SIM_US_STATES,
  arCityOptionLabel,
  buildGeoSimUrl,
  citiesForArProvince,
  clearGeoSimEligible,
  clearGeoSimSession,
  consumeGeoSimBoot,
  ctaTypeLabel,
  expectedTemplateForCountry,
  fetchWishlistConfigSnapshot,
  findArCityByGeoCode,
  findArCityEntry,
  isGeoSimEligibleMarked,
  markGeoSimEligible,
  mintGeoSimTokenFromApi,
  pinGeoSimParamsIfNeeded,
  provinceCityGroupsForSim,
  readGeoSimSession,
  resolveWishlistCtaFromEntries,
  simCityParamForEntry,
  useCanUseGeoSim,
  writeGeoSimSession,
  trackGeoSimEvent,
  registerGeoSimPersonProps,
  clearGeoSimPersonProps,
  edgeSnapshotProps,
  GEO_SIM_EVENTS,
  type GeoSimSession,
  type GeoSimVpnMode,
  type GeoSimPreset,
  type ResolvedWishlistCta,
  type WishlistConfigEntry,
} from "@/lib/geoSim";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { BRAND_SOURCE_KEY, BRAND_STORAGE_PREFIX } from "@/lib/brandConfig";
import {
  captureSiteLanguageSnapshot,
  isSiteDebugEnabled,
} from "@/lib/siteDebug";
import { cn } from "@/lib/utils";

const ADMIN_LINKS: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  superOnly?: boolean;
}[] = [
  { to: "/admin", label: "Admin", icon: LayoutDashboard },
  {
    to: CTA_POR_REGION_ADMIN_PATH,
    label: "CTA por región",
    icon: MapPin,
  },
  {
    to: "/admin/templates-por-pais",
    label: "Templates / VIP país",
    icon: Globe2,
  },
  { to: "/admin/reels", label: "Reels privados", icon: Film },
  {
    to: "/admin/memberships",
    label: "Membresías",
    icon: Shield,
    superOnly: true,
  },
  {
    to: "/admin/live-funnel",
    label: "Live funnel",
    icon: Activity,
    superOnly: true,
  },
  {
    to: "/admin/config-health",
    label: "Config health",
    icon: Settings2,
    superOnly: true,
  },
  {
    to: "/admin/worker-flags",
    label: "Worker / Edge",
    icon: Globe2,
    superOnly: true,
  },
];

type PanelTab = "geo" | "scenarios" | "links" | "debug";

function setSiteDebug(on: boolean) {
  try {
    const key = `${BRAND_STORAGE_PREFIX}_site_debug`;
    if (on) localStorage.setItem(key, "1");
    else {
      localStorage.removeItem(key);
      sessionStorage.removeItem(`${BRAND_STORAGE_PREFIX}_site_debug_session`);
    }
  } catch {
    /* ignore */
  }
}

function applyPresetToState(
  p: GeoSimPreset,
  setters: {
    setCountry: (v: string) => void;
    setState: (v: string) => void;
    setProvince: (v: string) => void;
    setCity: (v: string) => void;
    setSimulateVpn: (v: GeoSimVpnMode) => void;
  },
) {
  setters.setCountry(p.country);
  setters.setState(p.state || "");
  setters.setSimulateVpn(p.simulateVpn);
  if (p.cityGeoCode) {
    const e = findArCityByGeoCode(p.cityGeoCode);
    if (e) {
      setters.setProvince(e.provinceCode);
      setters.setCity(simCityParamForEntry(e));
      return;
    }
  }
  setters.setProvince(p.province || "");
  setters.setCity(p.city || "");
}

export function GeoSimToolbar() {
  // Without ClerkProvider, useIsSuperAdmin/useAdminAuth throw and kill the SPA.
  if (!getClerkPublishableKey()) return null;
  return <GeoSimToolbarInner />;
}

function GeoSimToolbarInner() {
  const { canUse, ready, signedIn } = useCanUseGeoSim();
  const isSuperAdmin = useIsSuperAdmin();
  const { getAuthHeaders } = useAdminAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<PanelTab>("scenarios");
  const [session, setSession] = useState<GeoSimSession | null>(null);
  const [country, setCountry] = useState("CL");
  const [state, setState] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [lang, setLang] = useState("");
  const [simulateVpn, setSimulateVpn] = useState<GeoSimVpnMode>("clean");
  const [simulateIp, setSimulateIp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emitted, setEmitted] = useState(() => getServerTemplateConfig());
  const [mounted, setMounted] = useState(false);
  const [bootMarked, setBootMarked] = useState(false);
  const [debugOn, setDebugOn] = useState(false);
  const [copied, setCopied] = useState(false);
  const [wishlistEntries, setWishlistEntries] = useState<
    WishlistConfigEntry[]
  >([]);
  /** Kill switch admin: anula todo CTA geo (Worker → global_hide). */
  const [hideGlobalHeroCta, setHideGlobalHeroCta] = useState(false);
  const [wishlistPreview, setWishlistPreview] =
    useState<ResolvedWishlistCta | null>(null);
  const lastCtaPreviewKey = useRef<string>("");

  const onAdmin = location.pathname.startsWith("/admin");
  const showToolbar =
    canUse || isSuperAdmin || bootMarked || isGeoSimEligibleMarked();

  useEffect(() => {
    setMounted(true);
    setDebugOn(isSiteDebugEnabled());
    const booted = consumeGeoSimBoot();
    if (booted || isGeoSimEligibleMarked()) {
      setBootMarked(true);
      markGeoSimEligible();
    }
    if (booted) {
      trackGeoSimEvent(GEO_SIM_EVENTS.boot, {
        boot_source: "geo_sim_boot_query",
        ...edgeSnapshotProps(),
      });
    }
  }, []);

  // Fire once when super_admin session becomes ready for toolbar
  useEffect(() => {
    if (!mounted || !ready || !signedIn || !(canUse || isSuperAdmin)) return;
    trackGeoSimEvent(GEO_SIM_EVENTS.boot, {
      boot_source: "auth_ready",
      is_super_admin: isSuperAdmin,
      can_use_geo_sim: canUse,
      on_admin: onAdmin,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per ready session
  }, [mounted, ready, signedIn, canUse, isSuperAdmin]);

  useEffect(() => {
    if (!showToolbar) return;
    const s = readGeoSimSession();
    setSession(s);
    if (s) {
      setCountry(s.country);
      setState(s.state || "");
      setProvince(s.province || "");
      setCity(s.city || "");
      setLang(s.lang || "");
      setSimulateVpn(s.simulateVpn || "clean");
      setSimulateIp(s.simulateIp || "");
      pinGeoSimParamsIfNeeded();
      registerGeoSimPersonProps(s);
    }
    setEmitted(getServerTemplateConfig());
  }, [showToolbar, location.pathname, location.search]);

  // Track active sim session + edge alignment (once per path change while active)
  useEffect(() => {
    if (!showToolbar || !session) return;
    const edge = edgeSnapshotProps();
    const det = String(edge.edge_detection_method || "");
    const edgeCc = String(edge.edge_country || "").toUpperCase();
    const aligned =
      det === "manual_override" && edgeCc === session.country.toUpperCase();

    trackGeoSimEvent(GEO_SIM_EVENTS.sessionActive, {
      simulated_country: session.country,
      simulated_province: session.province,
      simulated_city: session.city,
      simulated_state: session.state,
      simulate_vpn: session.simulateVpn,
      edge_aligned: aligned,
      ...edge,
    });

    if (!aligned) {
      trackGeoSimEvent(GEO_SIM_EVENTS.edgeMismatch, {
        simulated_country: session.country,
        simulated_province: session.province,
        simulated_city: session.city,
        ...edge,
      });
    }
  }, [showToolbar, session?.country, session?.province, session?.city, location.pathname]);

  useEffect(() => {
    if (!showToolbar || !session) return;
    pinGeoSimParamsIfNeeded();
  }, [showToolbar, session, location.pathname]);

  // Keep toolbar visible after boot even if Clerk still hydrating on home
  useEffect(() => {
    if (signedIn && (canUse || isSuperAdmin)) {
      markGeoSimEligible();
      setBootMarked(true);
    }
  }, [signedIn, canUse, isSuperAdmin]);

  const activatePath = onAdmin ? "/" : undefined;

  const arCities = useMemo(
    () => (country === "AR" && province ? citiesForArProvince(province) : []),
    [country, province],
  );

  const arCityGroups = useMemo(() => {
    if (country !== "AR" || !province) return [];
    return provinceCityGroupsForSim(province);
  }, [country, province]);

  const arCityMatch = useMemo(
    () =>
      country === "AR" ? findArCityEntry(province || null, city || null) : null,
    [country, province, city],
  );

  const expected = useMemo(
    () => expectedTemplateForCountry(country || session?.country || "US"),
    [country, session?.country],
  );

  // Live CTA = misma API pública que Worker + admin CTA por región
  useEffect(() => {
    if (!open && !session) return;
    let cancelled = false;
    void fetchWishlistConfigSnapshot().then((snap) => {
      if (cancelled) return;
      setWishlistEntries(snap.entries);
      setHideGlobalHeroCta(snap.hideGlobalHeroCta);
    });
    return () => {
      cancelled = true;
    };
  }, [open, session, location.pathname]);

  useEffect(() => {
    if (!wishlistEntries.length) {
      setWishlistPreview(null);
      return;
    }
    const cc = (session?.country || country || "").toUpperCase();
    if (!cc) {
      setWishlistPreview(null);
      return;
    }
    const resolved = resolveWishlistCtaFromEntries(wishlistEntries, {
      country: cc,
      province: session?.province || province || null,
      city: session?.city || city || null,
      hideGlobalHeroCta,
    });
    setWishlistPreview(resolved);
    // Track CTA preview only when geo/result changes (avoid PostHog spam)
    if (open && (country || session?.country)) {
      const key = [
        session?.country || country,
        session?.province || province,
        session?.city || city,
        resolved.source,
        resolved.geo_code,
        resolved.cta_type,
      ].join("|");
      if (key !== lastCtaPreviewKey.current) {
        lastCtaPreviewKey.current = key;
        trackGeoSimEvent(GEO_SIM_EVENTS.ctaPreview, {
          simulated_country: session?.country || country,
          simulated_province: session?.province || province || null,
          simulated_city: session?.city || city || null,
          wishlist_cta_type: resolved.cta_type,
          wishlist_cta_source: resolved.source,
          wishlist_geo_code: resolved.geo_code,
        });
      }
    }
  }, [
    wishlistEntries,
    hideGlobalHeroCta,
    session?.country,
    session?.province,
    session?.city,
    country,
    province,
    city,
    open,
  ]);

  const mismatch = useMemo(() => {
    if (!session) return false;
    const det = (emitted?.detectionMethod || "").toString();
    const cc = (emitted?.country || "").toUpperCase();
    if (det === "manual_override" && cc === session.country) return false;
    return true;
  }, [session, emitted]);

  const activate = useCallback(async () => {
    setError(null);
    const cc = country.trim().toUpperCase().slice(0, 2);
    if (!/^[A-Z]{2}$/.test(cc)) {
      setError("País ISO-2 inválido");
      return;
    }
    if (!signedIn) {
      setError("Iniciá sesión Clerk super_admin / QA para mint de gst");
      return;
    }
    setBusy(true);
    try {
      const mint = await mintGeoSimTokenFromApi(() => getAuthHeaders());
      if (!mint.ok) {
        const hint =
          mint.code === "GEO_SIM_SECRET_MISSING"
            ? " · Configurá GEO_SIM_SECRET en Railway + Worker (mismo valor)"
            : mint.status === 401 || mint.status === 403
              ? " · Rol super_admin o QA requerido"
              : "";
        trackGeoSimEvent(GEO_SIM_EVENTS.activateError, {
          error: mint.error,
          code: mint.code,
          http_status: mint.status,
          simulated_country: cc,
        });
        setError(mint.error + hint);
        setBusy(false);
        return;
      }

      // AR: province/city solo del catálogo CTA por región (nunca inventar)
      let simProvince =
        cc === "AR" && province.trim() ? province.trim() : null;
      let simCity = city.trim() || null;
      let cityGeoCode: string | null = null;
      if (cc === "AR") {
        const matched = findArCityEntry(simProvince, simCity);
        if (matched) {
          simProvince = matched.provinceCode;
          simCity = simCityParamForEntry(matched);
          cityGeoCode = matched.geo_code;
        } else if (simCity) {
          trackGeoSimEvent(GEO_SIM_EVENTS.activateError, {
            error: "city_not_in_catalog",
            simulated_country: cc,
            simulated_city: simCity,
            simulated_province: simProvince,
          });
          setError(
            `Ciudad “${simCity}” no existe en CTA por región (AR_CITY_CATALOG). Elegí del listado.`,
          );
          setBusy(false);
          return;
        } else if (simProvince) {
          const okProv = GEO_SIM_AR_PROVINCES.some(
            (p) => p.code === simProvince,
          );
          if (!okProv) {
            trackGeoSimEvent(GEO_SIM_EVENTS.activateError, {
              error: "province_not_in_catalog",
              simulated_country: cc,
              simulated_province: simProvince,
            });
            setError(
              `Provincia “${simProvince}” no es province_ar del admin.`,
            );
            setBusy(false);
            return;
          }
        }
      }

      const next: GeoSimSession = {
        country: cc,
        state: cc === "US" && state.trim() ? state.trim().toUpperCase() : null,
        province: simProvince,
        city: simCity,
        lang: lang.trim() || null,
        forceVariant: null,
        simulateVpn,
        simulateIp: simulateVpn === "clean" ? null : simulateIp.trim() || null,
        token: mint.token,
        activatedAt: Date.now(),
      };
      writeGeoSimSession(next);
      markGeoSimEligible();
      registerGeoSimPersonProps(next);
      trackGeoSimEvent(GEO_SIM_EVENTS.activate, {
        simulated_country: cc,
        simulated_state: next.state,
        simulated_province: next.province,
        simulated_city: next.city,
        city_ar_geo_code: cityGeoCode,
        simulate_vpn: next.simulateVpn,
        from_admin: onAdmin,
        expected_template: expected.templateName,
        expected_language: expected.language,
        wishlist_cta_type: wishlistPreview?.cta_type ?? null,
        wishlist_cta_source: wishlistPreview?.source ?? null,
      });

      const target = buildGeoSimUrl({
        path: activatePath || "/",
        country: next.country,
        token: next.token,
        state: next.state,
        province: next.province,
        city: next.city,
        lang: next.lang,
        simulateVpn: next.simulateVpn,
        simulateIp: next.simulateIp,
      });
      window.location.assign(target);
    } catch (e) {
      trackGeoSimEvent(GEO_SIM_EVENTS.activateError, {
        error: e instanceof Error ? e.message : "network_error",
        simulated_country: country,
      });
      setError(e instanceof Error ? e.message : "Error de red");
      setBusy(false);
    }
  }, [
    activatePath,
    city,
    country,
    expected.language,
    expected.templateName,
    getAuthHeaders,
    lang,
    onAdmin,
    province,
    signedIn,
    simulateIp,
    simulateVpn,
    state,
    wishlistPreview?.cta_type,
    wishlistPreview?.source,
  ]);

  const exit = useCallback(async () => {
    setBusy(true);
    try {
      await mintGeoSimTokenFromApi(() => getAuthHeaders(), { clear: true });
    } catch {
      /* non-fatal */
    } finally {
      clearGeoSimSession();
      clearGeoSimEligible();
      clearGeoSimPersonProps();
      trackGeoSimEvent(GEO_SIM_EVENTS.exit, {
        simulated_country: session?.country,
        simulated_province: session?.province,
        simulated_city: session?.city,
        simulate_vpn: session?.simulateVpn,
        ...edgeSnapshotProps(),
      });
      window.location.assign(onAdmin ? "/admin" : "/");
    }
  }, [getAuthHeaders, session, onAdmin]);

  const copyEdgeSnapshot = useCallback(async () => {
    const cfg = getServerTemplateConfig();
    const payload = {
      brand: BRAND_SOURCE_KEY,
      href: window.location.href,
      session: session
        ? {
            country: session.country,
            state: session.state,
            province: session.province,
            city: session.city,
            simulateVpn: session.simulateVpn,
          }
        : null,
      worker: cfg,
      expected,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackGeoSimEvent(GEO_SIM_EVENTS.snapshotCopy, {
        simulated_country: session?.country,
        ...edgeSnapshotProps(),
      });
    } catch {
      setError("No se pudo copiar al portapapeles");
    }
  }, [session, expected]);

  if (!mounted || !ready) return null;
  // Hide completely if never eligible (no role, no boot, no active session)
  if (!signedIn && !session && !bootMarked && !isGeoSimEligibleMarked()) {
    return null;
  }
  if (signedIn && !showToolbar && !session) return null;

  const active = !!session;
  const roleLabel = isSuperAdmin ? "super_admin" : canUse ? "qa" : "boot";

  const tabBtn = (id: PanelTab, label: string) => (
    <button
      key={id}
      type="button"
      onClick={() => {
        setTab(id);
        trackGeoSimEvent(GEO_SIM_EVENTS.tabChange, { tab: id });
      }}
      className={cn(
        "flex-1 rounded-lg px-1.5 py-1.5 text-[10px] font-semibold transition",
        tab === id
          ? "bg-violet-500 text-white shadow"
          : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-zinc-200",
      )}
    >
      {label}
    </button>
  );

  const setOpenTracked = (next: boolean | ((v: boolean) => boolean)) => {
    setOpen((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      if (value && !prev) {
        trackGeoSimEvent(GEO_SIM_EVENTS.toolbarOpen, {
          on_admin: onAdmin,
          has_session: !!session,
          ...edgeSnapshotProps(),
        });
      } else if (!value && prev) {
        trackGeoSimEvent(GEO_SIM_EVENTS.toolbarClose, { on_admin: onAdmin });
      }
      return value;
    });
  };

  const ui = (
    <div
      className={cn(
        "pointer-events-none fixed z-[2147483000] flex flex-col items-end gap-2 print:hidden",
        "right-3 sm:right-4",
        "bottom-[max(0.75rem,env(safe-area-inset-bottom))]",
      )}
      data-geo-sim-toolbar="1"
      data-super-admin-toolbar="1"
      data-brand={BRAND_SOURCE_KEY}
    >
      {active && (
        <div
          className={cn(
            "pointer-events-auto w-[min(100vw-1.5rem,20rem)] rounded-2xl border px-3 py-2.5 text-[11px] shadow-2xl backdrop-blur-xl",
            mismatch
              ? "border-amber-400/50 bg-amber-950/95 text-amber-50"
              : "border-emerald-400/35 bg-zinc-950/95 text-zinc-50",
          )}
          role="status"
        >
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold">
              {mismatch ? (
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-300" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-300" />
              )}
              Geo sim · {session.country}
            </span>
            <span className="rounded-full bg-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide text-zinc-300">
              {roleLabel}
            </span>
          </div>
          <div className="space-y-0.5 font-mono text-[10px] leading-relaxed text-zinc-300">
            <div>
              Viewing as:{" "}
              <strong className="text-white">{session.country}</strong>
              {session.state ? ` · ${session.state}` : ""}
              {session.province ? ` · ${session.province}` : ""}
              {session.city ? ` · ${session.city}` : ""}
            </div>
            <div>
              Esperado: {expected.templateName} · {expected.language}
            </div>
            <div>
              Edge: {emitted?.templateName || "—"} · {emitted?.country || "—"} ·{" "}
              <strong
                className={
                  emitted?.detectionMethod === "manual_override"
                    ? "text-emerald-300"
                    : "text-amber-300"
                }
              >
                {emitted?.detectionMethod || "—"}
              </strong>
            </div>
            <div>VPN: {session.simulateVpn}</div>
            {wishlistPreview && (
              <div className="pt-0.5 text-violet-200/90">
                CTA admin:{" "}
                <strong className="text-white">{wishlistPreview.label}</strong>
                {" · "}
                {wishlistPreview.source}
                {wishlistPreview.geo_code
                  ? ` · ${wishlistPreview.geo_code}`
                  : ""}
              </div>
            )}
            {/* Edge real: qué aplicó el Worker (puede diferir del preview admin). */}
            {emitted && (
              <div
                className={
                  hideGlobalHeroCta ||
                  emitted.ctaResolvedFrom === "global_hide" ||
                  emitted.hideCta
                    ? "pt-0.5 text-amber-200/95"
                    : "pt-0.5 text-emerald-200/90"
                }
              >
                Edge CTA:{" "}
                <strong className="text-white">
                  {emitted.hideCta
                    ? "oculto"
                    : emitted.showCommunity
                      ? "Exclusivo"
                      : emitted.showBirthday
                        ? "Comunidad"
                        : emitted.showWishlist
                          ? "Wishlist"
                          : emitted.showDualVideo
                            ? "Video Dual"
                            : "sin CTA"}
                </strong>
                {emitted.ctaResolvedFrom
                  ? ` · ${emitted.ctaResolvedFrom}`
                  : ""}
                {emitted.ctaResolvedMatched
                  ? ` · ${emitted.ctaResolvedMatched}`
                  : ""}
              </div>
            )}
          </div>
          {(hideGlobalHeroCta ||
            emitted?.ctaResolvedFrom === "global_hide") && (
            <p className="mt-1.5 text-[10px] leading-snug text-amber-100/95">
              Kill switch{" "}
              <strong>Hero CTAs ocultos</strong> activo en{" "}
              <Link
                to={CTA_POR_REGION_ADMIN_PATH}
                className="underline decoration-amber-300/60 underline-offset-2"
              >
                CTA por región
              </Link>
              . Anula city_ar / province_ar (ej. Mar del Plata → Exclusivo no
              se muestra). Apagá el switch para que el edge aplique el CTA
              asignado.
            </p>
          )}
          {mismatch && (
            <p className="mt-1.5 text-[10px] leading-snug text-amber-100/90">
              Worker no aplicó override. Revisá GEO_SIM_SECRET (Railway =
              Cloudflare) y re-activá con mint fresco.
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => {
                setTab("geo");
                setOpenTracked(true);
              }}
              className="min-h-[32px] rounded-lg border border-white/15 bg-white/10 px-2.5 text-[10px] font-medium hover:bg-white/15"
            >
              Cambiar
            </button>
            <button
              type="button"
              onClick={() => void exit()}
              disabled={busy}
              className="min-h-[32px] rounded-lg border border-white/15 bg-white/5 px-2.5 text-[10px] font-medium hover:bg-white/10 disabled:opacity-50"
            >
              {busy ? "…" : "Reset"}
            </button>
            <Link
              to="/admin"
              className="inline-flex min-h-[32px] items-center rounded-lg border border-violet-400/30 bg-violet-500/20 px-2.5 text-[10px] font-medium text-violet-100 hover:bg-violet-500/30"
            >
              Admin
            </Link>
          </div>
        </div>
      )}

      {open && (
        <div
          className={cn(
            "pointer-events-auto w-[min(100vw-1.5rem,22rem)] overflow-hidden rounded-2xl",
            "border border-white/12 bg-zinc-950/97 text-zinc-100 shadow-2xl backdrop-blur-xl",
          )}
        >
          <div className="flex items-center justify-between gap-2 border-b border-white/[0.07] px-3 py-2.5">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-white">
                <Sparkles className="h-3.5 w-3.5 text-violet-300" />
                🌎 Simular visitante
              </p>
              <p className="truncate font-mono text-[9px] text-zinc-500">
                {BRAND_SOURCE_KEY} · {roleLabel} · GeoSim
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpenTracked(false)}
              className="rounded-lg p-1.5 hover:bg-white/10"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-1 border-b border-white/[0.06] p-1.5">
            {tabBtn("scenarios", "Escenarios")}
            {tabBtn("geo", "Geo")}
            {isSuperAdmin && tabBtn("links", "Atajos")}
            {isSuperAdmin && tabBtn("debug", "Debug")}
          </div>

          <div className="max-h-[min(70vh,30rem)] overflow-y-auto overscroll-contain p-3">
            {tab === "scenarios" && (
              <div className="space-y-2">
                <p className="text-[10px] leading-snug text-zinc-500">
                  Escenarios canónicos de esta brand. Default visitante real =
                  VPN Clean. Mint gst vía API (no inventar token).
                </p>
                {GEO_SIM_SCENARIO_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      applyPresetToState(p, {
                        setCountry,
                        setState,
                        setProvince,
                        setCity,
                        setSimulateVpn,
                      });
                      trackGeoSimEvent(GEO_SIM_EVENTS.presetSelect, {
                        preset_id: p.id,
                        preset_label: p.label,
                        simulated_country: p.country,
                        simulated_province: p.province,
                        city_ar_geo_code: p.cityGeoCode,
                        simulate_vpn: p.simulateVpn,
                      });
                      setTab("geo");
                    }}
                    className={cn(
                      "flex w-full flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition",
                      "border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.07]",
                      p.simulateVpn !== "clean" &&
                        "border-amber-500/30 bg-amber-950/30",
                    )}
                  >
                    <span className="text-[12px] font-semibold text-zinc-100">
                      {p.label}
                    </span>
                    <span className="text-[10px] text-zinc-500">{p.intent}</span>
                    <span className="font-mono text-[9px] text-zinc-600">
                      {p.country}
                      {p.state ? ` · state=${p.state}` : ""}
                      {p.province ? ` · province_ar=${p.province}` : ""}
                      {p.cityGeoCode
                        ? ` · city_ar=${p.cityGeoCode}`
                        : p.city
                          ? ` · city=${p.city}`
                          : ""}{" "}
                      · vpn={p.simulateVpn}
                    </span>
                  </button>
                ))}
                <Link
                  to={CTA_POR_REGION_ADMIN_PATH}
                  onClick={() => setOpen(false)}
                  className="flex min-h-[40px] items-center justify-center gap-2 rounded-xl border border-violet-400/30 bg-violet-500/15 text-[11px] font-medium text-violet-100 hover:bg-violet-500/25"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  Abrir CTA por región (admin)
                </Link>
              </div>
            )}

            {tab === "geo" && (
              <div className="space-y-2">
                <p className="text-[10px] leading-snug text-zinc-500">
                  Simulación edge real (HMAC). Full navigation al Worker.
                </p>
                <label className="block text-[10px] font-medium text-zinc-400">
                  País ISO-2
                </label>
                <div className="flex flex-wrap gap-1">
                  {GEO_SIM_QUICK_COUNTRIES.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => setCountry(c.code)}
                      className={cn(
                        "min-h-[28px] rounded-md px-2 text-[10px] font-semibold",
                        country === c.code
                          ? "bg-violet-500 text-white"
                          : "bg-white/10 text-zinc-300 hover:bg-white/15",
                      )}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <input
                  value={country}
                  onChange={(e) =>
                    setCountry(e.target.value.toUpperCase().slice(0, 2))
                  }
                  maxLength={2}
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 font-mono text-xs"
                  placeholder="CL"
                />

                {country === "US" && (
                  <>
                    <label className="block text-[10px] text-zinc-400">
                      Estado US
                    </label>
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs"
                    >
                      <option value="">—</option>
                      {GEO_SIM_US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {country === "AR" && (
                  <>
                    <p className="rounded-lg bg-white/[0.04] px-2 py-1.5 text-[9px] leading-snug text-zinc-500">
                      1:1 con{" "}
                      <Link
                        to={CTA_POR_REGION_ADMIN_PATH}
                        className="text-violet-300 underline"
                        onClick={() => setOpen(false)}
                      >
                        /admin/wishlist-config
                      </Link>{" "}
                      (CTA por región). province_ar + city_ar del catálogo
                      real. Barrios = city_ar (no hay neighborhood).
                    </p>
                    <label className="block text-[10px] font-medium text-zinc-400">
                      Atajos = geo_code del admin
                    </label>
                    <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto">
                      {GEO_SIM_AR_QUICK_CITIES.map((q) => (
                        <button
                          key={q.geoCode}
                          type="button"
                          onClick={() => {
                            setProvince(q.province);
                            setCity(q.city);
                          }}
                          className={cn(
                            "min-h-[26px] rounded-md px-2 text-[9px] font-medium",
                            arCityMatch?.geo_code === q.geoCode
                              ? "bg-violet-500 text-white"
                              : "bg-white/10 text-zinc-300 hover:bg-white/15",
                          )}
                          title={`city_ar.geo_code=${q.geoCode}`}
                        >
                          {q.label}
                        </button>
                      ))}
                    </div>
                    <label className="block text-[10px] text-zinc-400">
                      province_ar ({GEO_SIM_AR_PROVINCES.length} · admin)
                    </label>
                    <select
                      value={province}
                      onChange={(e) => {
                        setProvince(e.target.value);
                        setCity("");
                      }}
                      className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs"
                    >
                      <option value="">— solo país AR (sin province) —</option>
                      {GEO_SIM_AR_PROVINCES.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.name} · {p.region}
                        </option>
                      ))}
                    </select>
                    <label className="block text-[10px] text-zinc-400">
                      city_ar (ciudad / barrio / partido)
                      {province
                        ? ` · ${arCities.length} filas catálogo`
                        : " · elegí provincia"}
                    </label>
                    <select
                      value={arCityMatch?.geo_code ?? ""}
                      onChange={(e) => {
                        const geo = e.target.value;
                        if (!geo) {
                          setCity("");
                          return;
                        }
                        const entry =
                          findArCityByGeoCode(geo) ||
                          arCities.find((c) => c.geo_code === geo);
                        if (entry) {
                          setProvince(entry.provinceCode);
                          setCity(simCityParamForEntry(entry));
                        }
                      }}
                      disabled={!province}
                      className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs disabled:opacity-40"
                    >
                      <option value="">
                        — solo province_ar (sin city_ar) —
                      </option>
                      {arCityGroups[0]?.regions.map((reg) => (
                        <optgroup key={reg.region} label={reg.region}>
                          {reg.cities.map((c) => (
                            <option key={c.geo_code} value={c.geo_code}>
                              {arCityOptionLabel(c)}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      {arCityGroups.length === 0 &&
                        arCities.map((c) => (
                          <option key={c.geo_code} value={c.geo_code}>
                            {arCityOptionLabel(c)}
                          </option>
                        ))}
                    </select>
                    {arCityMatch && (
                      <p className="font-mono text-[9px] leading-relaxed text-emerald-400/90">
                        admin city_ar.geo_code=
                        <strong>{arCityMatch.geo_code}</strong>
                        {" · "}province_ar=
                        <strong>{arCityMatch.provinceCode}</strong>
                        {" · "}region={arCityMatch.region}
                        {" · "}cf.city sim=
                        <strong>{simCityParamForEntry(arCityMatch)}</strong>
                      </p>
                    )}
                    {city && !arCityMatch && (
                      <p className="text-[9px] text-amber-300/90">
                        “{city}” NO está en el catálogo de CTA por región — no
                        uses valores inventados.
                      </p>
                    )}
                    {wishlistPreview && (
                      <div className="rounded-xl border border-violet-400/25 bg-violet-500/10 px-2.5 py-2 text-[10px] leading-snug text-violet-100">
                        <div className="font-semibold">
                          CTA geo (wishlist-config) →{" "}
                          {ctaTypeLabel(String(wishlistPreview.cta_type))}
                        </div>
                        <div className="mt-0.5 font-mono text-[9px] text-violet-200/80">
                          source={wishlistPreview.source}
                          {wishlistPreview.geo_code
                            ? ` · match=${wishlistPreview.geo_code}`
                            : ""}
                        </div>
                        {hideGlobalHeroCta ||
                        emitted?.ctaResolvedFrom === "global_hide" ? (
                          <div className="mt-1 text-[9px] font-medium text-amber-200">
                            ⚠ Kill switch global ON → edge fuerza hideCta
                            (ctaResolvedFrom=global_hide). El CTA geo no se
                            pinta hasta apagar “Hero CTAs ocultos”.
                          </div>
                        ) : (
                          <div className="mt-1 text-[9px] text-violet-200/60">
                            Cascada Worker: city_ar &gt; province_ar &gt;
                            country (salvo kill switch global)
                          </div>
                        )}
                        {emitted && (
                          <div className="mt-1 font-mono text-[9px] text-zinc-300/90">
                            edge: hideCta={String(!!emitted.hideCta)} ·
                            showCommunity={String(!!emitted.showCommunity)} ·
                            from={emitted.ctaResolvedFrom || "—"}
                            {emitted.ctaResolvedMatched
                              ? ` · matched=${emitted.ctaResolvedMatched}`
                              : ""}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {country && country !== "AR" && wishlistPreview && (
                  <div className="rounded-xl border border-violet-400/25 bg-violet-500/10 px-2.5 py-2 text-[10px] text-violet-100">
                    CTA country{" "}
                    <strong>{wishlistPreview.geo_code || country}</strong>:{" "}
                    {wishlistPreview.label} ({wishlistPreview.source})
                  </div>
                )}

                <label className="block text-[10px] text-zinc-400">
                  Idioma override
                </label>
                <input
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs"
                  placeholder="en / es / de"
                />

                <label className="block text-[10px] text-zinc-400">
                  Modo VPN
                </label>
                <select
                  value={simulateVpn}
                  onChange={(e) =>
                    setSimulateVpn(e.target.value as GeoSimVpnMode)
                  }
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-xs"
                >
                  <option value="clean">clean — visitante real</option>
                  <option value="anonymous">anonymous — QA privacy</option>
                  <option value="hosting">hosting — datacenter</option>
                </select>

                <label className="block text-[10px] text-zinc-400">
                  simulate_ip (solo si no es clean)
                </label>
                <input
                  value={simulateIp}
                  onChange={(e) => setSimulateIp(e.target.value)}
                  disabled={simulateVpn === "clean"}
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 font-mono text-xs disabled:opacity-40"
                  placeholder="185.220.101.1"
                />

                {simulateVpn !== "clean" && (
                  <p className="rounded-lg bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-200">
                    No reportar Anonymous/hosting como “visitante real”.
                  </p>
                )}

                {error && (
                  <p className="rounded-xl bg-red-500/15 px-2.5 py-2 text-[10px] leading-snug text-red-200">
                    {error}
                  </p>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => void activate()}
                    disabled={busy || !signedIn}
                    className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-violet-500 px-3 text-xs font-semibold text-white hover:bg-violet-400 disabled:opacity-50 active:scale-[0.98]"
                  >
                    {busy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Globe2 className="h-3.5 w-3.5" />
                    )}
                    {onAdmin ? "Activar en sitio" : "Aplicar"}
                  </button>
                  {active && (
                    <button
                      type="button"
                      onClick={() => void exit()}
                      disabled={busy}
                      className="min-h-[44px] rounded-xl border border-white/15 px-3 text-xs hover:bg-white/5"
                    >
                      Reset
                    </button>
                  )}
                </div>
                {!signedIn && (
                  <p className="text-[10px] text-amber-200/90">
                    Sin sesión Clerk no hay mint.{" "}
                    <a href="/admin" className="underline">
                      Login admin
                    </a>
                  </p>
                )}
              </div>
            )}

            {tab === "links" && isSuperAdmin && (
              <div className="space-y-1.5">
                <p className="mb-2 text-[10px] text-zinc-500">
                  Atajos · brand {BRAND_SOURCE_KEY}
                </p>
                {ADMIN_LINKS.filter((l) => !l.superOnly || isSuperAdmin).map(
                  (l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className="flex min-h-[44px] items-center gap-2.5 rounded-xl border border-white/[0.07] bg-white/[0.03] px-3 text-[12px] font-medium text-zinc-100 transition hover:bg-white/[0.07]"
                    >
                      <l.icon className="h-4 w-4 shrink-0 text-violet-300" />
                      <span className="flex-1">{l.label}</span>
                      <ExternalLink className="h-3 w-3 text-zinc-500" />
                    </Link>
                  ),
                )}
                <a
                  href="/?geo_sim_boot=1"
                  className="flex min-h-[44px] items-center gap-2.5 rounded-xl border border-violet-400/25 bg-violet-500/10 px-3 text-[12px] font-medium text-violet-100 hover:bg-violet-500/20"
                >
                  <Globe2 className="h-4 w-4 text-violet-300" />
                  <span className="flex-1">Boot GeoSim en home</span>
                </a>
              </div>
            )}

            {tab === "debug" && isSuperAdmin && (
              <div className="space-y-3">
                <p className="text-[10px] leading-snug text-zinc-500">
                  Diagnóstico idioma/geo en consola (prod).
                </p>
                <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3">
                  <div className="min-w-0">
                    <span className="block text-[12px] font-medium">
                      Site debug
                    </span>
                    <span className="block text-[9px] text-zinc-500">
                      localStorage · ?debug=site
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 accent-violet-500"
                    checked={debugOn}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setSiteDebug(on);
                      setDebugOn(on);
                      trackGeoSimEvent(GEO_SIM_EVENTS.debugToggle, {
                        site_debug_on: on,
                      });
                      if (on) captureSiteLanguageSnapshot("toolbar enable");
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => captureSiteLanguageSnapshot("toolbar log")}
                  className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-white/10 text-[12px] font-medium hover:bg-white/15"
                >
                  Log i18n/geo ahora
                </button>
                <button
                  type="button"
                  onClick={() => void copyEdgeSnapshot()}
                  className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-white/12 text-[12px] font-medium hover:bg-white/5"
                >
                  <Copy className="h-3.5 w-3.5" />
                  {copied ? "Copiado" : "Copiar snapshot edge"}
                </button>
                <div className="rounded-xl bg-black/40 p-2.5 font-mono text-[9px] leading-relaxed text-zinc-400">
                  <div>
                    href:{" "}
                    {typeof window !== "undefined"
                      ? window.location.pathname
                      : ""}
                  </div>
                  <div>
                    tpl: {emitted?.templateName || "—"} /{" "}
                    {emitted?.country || "—"}
                  </div>
                  <div>det: {emitted?.detectionMethod || "—"}</div>
                  <div>lang: {emitted?.language || "—"}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!active && (
        <button
          type="button"
          onClick={() => setOpenTracked((v) => !v)}
          className={cn(
            "pointer-events-auto inline-flex min-h-[44px] items-center gap-2 rounded-full",
            "border border-violet-400/45 bg-violet-600 px-3.5 py-2.5",
            "text-xs font-semibold text-white shadow-lg shadow-violet-950/50",
            "transition hover:bg-violet-500 active:scale-[0.98]",
          )}
        >
          <Globe2 className="h-3.5 w-3.5" />
          {isSuperAdmin || canUse
            ? "🌎 Simular visitante"
            : "GeoSim (login)"}
          <ChevronDown
            className={cn("h-3 w-3 transition", open && "rotate-180")}
          />
        </button>
      )}

      {active && !open && (
        <button
          type="button"
          onClick={() => setOpenTracked(true)}
          className={cn(
            "pointer-events-auto inline-flex min-h-[40px] items-center gap-1.5 rounded-full",
            "border border-emerald-400/40 bg-emerald-700/90 px-3 py-2",
            "text-[11px] font-semibold text-white shadow-lg",
          )}
        >
          <Globe2 className="h-3.5 w-3.5" />
          {session.country} · tools
        </button>
      )}
    </div>
  );

  return createPortal(ui, document.body);
}
